import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import SortHeader from '../../components/SortHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { GBP, GBP0 } from '../proposals/constants';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import CostEntryDialog from './CostEntryDialog';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine, ReferenceDot,
} from 'recharts';
import { TrendingUp, PoundSterling, PlusCircle, Pencil, AlertTriangle, CheckCircle2 } from 'lucide-react';

const TEAL  = '#3BC6B4';
const NAVY  = '#0B3C5D';
const AMBER = '#F59E0B';
const HORIZONS = [3, 6, 12, 24];

function Stat({ label, value, sub, tint = 'text-[color:var(--bm-text)]' }) {
    return (
        <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`font-display text-[20px] font-semibold mt-1 leading-none ${tint}`}>{value}</div>
            {sub && <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mt-0.5">{sub}</div>}
        </div>
    );
}

function Section({ title, children, icon: Icon, action }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[color:var(--bm-text)]">
                    {Icon && <Icon size={16} className="text-[color:var(--bm-primary)]" />} {title}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload.reduce((acc, x) => ({ ...acc, [x.dataKey]: x.value }), {});
    const revenue = p.revenue_actual ?? p.revenue_forecast ?? 0;
    const cost = p.cost_actual ?? p.cost_forecast ?? 0;
    const cumulativeGp = p.cumulative_actual ?? p.cumulative_forecast ?? 0;
    const monthGp = p.gp_actual ?? p.gp_forecast ?? 0;
    return (
        <div className="bg-white border border-[color:var(--bm-border)] rounded-lg shadow-lg px-3 py-2.5 text-[12.5px]">
            <div className="font-semibold text-[color:var(--bm-text)] mb-1">{label}</div>
            <div className="flex items-center justify-between gap-4"><span className="text-[color:var(--bm-text-muted)]">Revenue</span><span className="font-medium">{GBP(revenue)}</span></div>
            <div className="flex items-center justify-between gap-4"><span className="text-[color:var(--bm-text-muted)]">Cost</span><span className="font-medium">{GBP(cost)}</span></div>
            <div className="flex items-center justify-between gap-4"><span className="text-[color:var(--bm-text-muted)]">GP (running total)</span><span className="font-semibold" style={{ color: NAVY }}>{GBP(cumulativeGp)}</span></div>
            <div className="flex items-center justify-between gap-4"><span className="text-[color:var(--bm-text-muted)]">This month's GP</span><span className="font-medium">{GBP(monthGp)}</span></div>
        </div>
    );
};

export default function Profitability() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const canCreate = can('finance', 'create');
    const canEdit = can('finance', 'edit');

    const [summary, setSummary] = useState(null);
    const [clients, setClients] = useState([]);
    const [forecast, setForecast] = useState(null);
    const [horizon, setHorizon] = useState(12);
    const [chartClientId, setChartClientId] = useState('all');
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState('revenue_to_date');
    const [sortDir, setSortDir] = useState('desc');
    const [overrideEdits, setOverrideEdits] = useState({});
    const [logCostOpen, setLogCostOpen] = useState(false);
    const [gridEntry, setGridEntry] = useState(null); // { clientId, period, entry }

    const loadBase = useCallback(async () => {
        const [s, c, st] = await Promise.all([
            api.get('/profitability/summary'),
            api.get('/profitability/clients'),
            api.get('/settings'),
        ]);
        setSummary(s.data);
        setClients(c.data.clients);
        setSettings(st.data);
    }, []);

    const loadForecast = useCallback(async () => {
        const params = { months: horizon };
        if (chartClientId !== 'all') params.client_id = chartClientId;
        const f = await api.get('/profitability/forecast', { params });
        setForecast(f.data);
    }, [horizon, chartClientId]);

    const loadAll = useCallback(async () => {
        await Promise.all([loadBase(), loadForecast()]);
        setLoading(false);
    }, [loadBase, loadForecast]);

    useEffect(() => { loadAll().catch(e => toast.error(formatApiError(e?.response?.data?.detail) || e.message)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (loading) return; // initial loadAll already covers the first fetch
        loadForecast().catch(e => toast.error(formatApiError(e?.response?.data?.detail) || e.message));
    }, [horizon, chartClientId, loading, loadForecast]);

    const [rawClients, setRawClients] = useState([]);
    useEffect(() => {
        api.get('/clients', { params: { per_page: 200 } }).then(r => setRawClients(r.data.items || [])).catch(() => {});
    }, []);

    const changeSort = (key) => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    };

    const sortedClients = useMemo(() => {
        const arr = [...clients];
        arr.sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            return sortDir === 'asc' ? (av - bv) : (bv - av);
        });
        return arr;
    }, [clients, sortKey, sortDir]);

    const chartData = useMemo(() => {
        if (!forecast) return [];
        const boundaryIndex = forecast.months.findIndex(m => m.is_forecast);
        return forecast.months.map((m, i) => ({
            period: m.period,
            label: m.label,
            revenue_actual: !m.is_forecast ? m.revenue : null,
            cost_actual: !m.is_forecast ? m.cost : null,
            revenue_forecast: m.is_forecast ? m.revenue : null,
            cost_forecast: m.is_forecast ? m.cost : null,
            gp_actual: boundaryIndex === -1 || i <= boundaryIndex ? m.gp : null,
            gp_forecast: boundaryIndex !== -1 && i >= boundaryIndex - 1 ? m.gp : null,
            cumulative_actual: boundaryIndex === -1 || i <= boundaryIndex ? m.cumulative_gp : null,
            cumulative_forecast: boundaryIndex !== -1 && i >= boundaryIndex - 1 ? m.cumulative_gp : null,
            is_forecast: m.is_forecast,
        }));
    }, [forecast]);

    const boundaryLabel = useMemo(() => {
        const b = chartData.find(m => m.is_forecast);
        return b ? b.label : null;
    }, [chartData]);

    // Points in the projected future where the running cumulative GP crosses
    // zero — flagged as loss (going negative) or breakeven (recovering to
    // positive/zero). Driven by cumulative_gp (all-time running total), not
    // any single month's standalone gp — a client can be cash-flow-positive
    // in a given month while still deep in an all-time deficit.
    const breakevenPoints = useMemo(() => {
        if (!forecast) return [];
        const arr = forecast.months;
        const points = [];
        for (let i = 1; i < arr.length; i++) {
            if (!arr[i].is_forecast) continue;
            const prev = arr[i - 1].cumulative_gp, cur = arr[i].cumulative_gp;
            if (prev >= 0 && cur < 0) points.push({ label: arr[i].label, type: 'loss', cumulative_gp: cur });
            else if (prev < 0 && cur >= 0) points.push({ label: arr[i].label, type: 'breakeven', cumulative_gp: cur });
        }
        return points;
    }, [forecast]);

    // Honest "no" — the numbers genuinely don't recover within this horizon.
    const noBreakevenInWindow = useMemo(() => {
        if (!forecast || !forecast.months.length) return false;
        const last = forecast.months[forecast.months.length - 1];
        const hasBreakeven = breakevenPoints.some(p => p.type === 'breakeven');
        return last.cumulative_gp < 0 && !hasBreakeven;
    }, [forecast, breakevenPoints]);

    const saveOverride = async (clientId, value) => {
        const num = value === '' ? null : parseFloat(value);
        try {
            await api.patch(`/clients/${clientId}`, { profitability_forecast_override: num });
            toast.success('Forecast override saved');
            // Refetch rather than optimistically patch local state — when clearing an
            // override (num=null) the correct new value is the resolved trailing
            // average / settings default, which only the server can compute.
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    // ── Monthly quick-entry grid ──
    const gridMonths = useMemo(() => {
        const out = [];
        const today = new Date();
        for (let i = 5; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
        return out;
    }, []);
    const [entriesByClientMonth, setEntriesByClientMonth] = useState({});
    const loadGridEntries = useCallback(async () => {
        try {
            const r = await api.get('/profitability/cost-entries', {
                params: { period_from: gridMonths[0], period_to: gridMonths[gridMonths.length - 1] },
            });
            const map = {};
            (r.data.items || []).forEach(e => { map[`${e.client_id}__${e.period}`] = e; });
            setEntriesByClientMonth(map);
        } catch { /* non-fatal */ }
    }, [gridMonths]);
    useEffect(() => { loadGridEntries(); }, [loadGridEntries]);

    const refreshAfterCostChange = () => { loadAll(); loadGridEntries(); };

    if (loading) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading profitability…</div>;

    return (
        <div data-testid="profitability-page" className="bm-fade-in">
            <PageHeader
                title="Profitability"
                description="Per-client gross profit from invoices & recurring billing, with a forward-looking forecast."
                actions={canCreate && (
                    <Button onClick={() => setLogCostOpen(true)} style={{ background: 'var(--bm-primary)' }} data-testid="log-cost-btn">
                        <PlusCircle size={15} className="mr-1.5" /> Log cost
                    </Button>
                )}
            />

            <div className="px-8 py-7 space-y-7">
                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Stat label="Revenue to date" value={GBP0(summary.total_revenue_to_date)} />
                    <Stat label="Cost to date" value={GBP0(summary.total_cost_to_date)} />
                    <Stat label="GP to date" value={GBP0(summary.total_gp_to_date)} tint="text-[color:var(--bm-accent-600)]" />
                    <Stat label="Blended GP%" value={`${summary.blended_gp_pct}%`} tint="text-emerald-600" />
                    <Stat label="Avg cost/client/mo" value={GBP(summary.avg_monthly_cost_per_client)} />
                </div>

                {/* Chart */}
                <Section title={chartClientId === 'all' ? 'Revenue, cost & gross profit' : `Revenue, cost & gross profit — ${clients.find(c => c.client_id === chartClientId)?.client_name || ''}`} icon={TrendingUp}
                    action={
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <Select value={chartClientId} onValueChange={setChartClientId}>
                                <SelectTrigger className="h-8 w-[180px] text-[12px]" data-testid="chart-client-filter">
                                    <SelectValue placeholder="All clients" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All clients</SelectItem>
                                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1 p-1 rounded-lg bg-[color:var(--bm-bg-mute)]" data-testid="horizon-selector">
                                {HORIZONS.map(h => (
                                    <button key={h} onClick={() => setHorizon(h)} data-testid={`horizon-${h}`}
                                            className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition ${horizon === h ? 'bg-white shadow-sm text-[color:var(--bm-text)]' : 'text-[color:var(--bm-text-muted)]'}`}>
                                        {h}mo
                                    </button>
                                ))}
                            </div>
                        </div>
                    }>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                        <Stat label="Revenue to date" value={GBP0(forecast.to_date.revenue)} />
                        <Stat label="Cost to date" value={GBP0(forecast.to_date.cost)} />
                        <Stat label="GP to date" value={GBP0(forecast.to_date.gp)} tint={forecast.to_date.gp < 0 ? 'text-red-600' : 'text-[color:var(--bm-accent-600)]'} />
                        <Stat label="GP% to date" value={`${forecast.to_date.gp_pct}%`} tint="text-emerald-600" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <Stat label={`Forecast revenue (${horizon}mo)`} value={GBP0(forecast.forecast_totals.revenue)} />
                        <Stat label={`Forecast cost (${horizon}mo)`} value={GBP0(forecast.forecast_totals.cost)} />
                        <Stat label={`Forecast GP (${horizon}mo)`} value={GBP0(forecast.forecast_totals.gp)} tint={forecast.forecast_totals.gp < 0 ? 'text-red-600' : 'text-[color:var(--bm-accent-600)]'} />
                        <Stat label="Forecast GP%" value={`${forecast.forecast_totals.gp_pct}%`} tint={forecast.forecast_totals.gp_pct < 0 ? 'text-red-600' : 'text-emerald-600'} />
                    </div>
                    {breakevenPoints.length > 0 && (
                        <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="breakeven-flags">
                            {breakevenPoints.map((p, i) => (
                                <span key={i} className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full ${p.type === 'loss' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                    {p.type === 'loss' ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
                                    {p.type === 'loss' ? 'Turns to a loss' : 'Back to breakeven'} — {p.label}
                                </span>
                            ))}
                        </div>
                    )}
                    {noBreakevenInWindow && (
                        <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="no-breakeven-flag">
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                                <AlertTriangle size={12} />
                                Not projected to break even within the next {horizon} months at the current forecast run rate — try a longer horizon, or review this client's Forecast £/mo figure.
                            </span>
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E3E8ED" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#8A95A3' }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={v => `£${v >= 1000 || v <= -1000 ? (v / 1000).toFixed(0) + 'k' : v}`} tick={{ fontSize: 11, fill: '#8A95A3' }} axisLine={false} tickLine={false} width={55} />
                            <Tooltip content={<ChartTooltip />} />
                            <ReferenceLine y={0} stroke="#C7CDD6" />
                            {boundaryLabel && <ReferenceLine x={boundaryLabel} stroke="#8A95A3" strokeDasharray="4 4" label={{ value: 'Today', position: 'insideTopRight', fontSize: 11, fill: '#8A95A3' }} />}
                            <Bar dataKey="revenue_actual" stackId="rev" fill={NAVY} name="Revenue" radius={[3, 3, 0, 0]} barSize={16} />
                            <Bar dataKey="revenue_forecast" stackId="rev" fill={NAVY} fillOpacity={0.35} name="Revenue (forecast)" radius={[3, 3, 0, 0]} barSize={16} />
                            <Bar dataKey="cost_actual" stackId="cost" fill={AMBER} name="Cost" radius={[3, 3, 0, 0]} barSize={16} />
                            <Bar dataKey="cost_forecast" stackId="cost" fill={AMBER} fillOpacity={0.35} name="Cost (forecast)" radius={[3, 3, 0, 0]} barSize={16} />
                            <Line type="monotone" dataKey="cumulative_actual" stroke={TEAL} strokeWidth={2.5} dot={false} name="GP" connectNulls />
                            <Line type="monotone" dataKey="cumulative_forecast" stroke={TEAL} strokeWidth={2.5} strokeDasharray="6 4" dot={false} name="GP (forecast)" connectNulls />
                            {breakevenPoints.map((p, i) => (
                                <ReferenceDot key={i} x={p.label} y={p.cumulative_gp} r={5}
                                    fill={p.type === 'loss' ? '#DC2626' : '#059669'} stroke="white" strokeWidth={1.5}
                                    label={{ value: p.type === 'loss' ? 'Loss' : 'Breakeven', position: 'top', fontSize: 10.5, fill: p.type === 'loss' ? '#DC2626' : '#059669' }} />
                            ))}
                        </ComposedChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 text-[11.5px] text-[color:var(--bm-text-muted)] mt-3">
                        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: NAVY, display: 'inline-block' }} /> Revenue</span>
                        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: AMBER, display: 'inline-block' }} /> Cost</span>
                        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: 2, background: TEAL, display: 'inline-block' }} /> Gross profit</span>
                        <span className="opacity-60">· lighter shade / dashed = forecast</span>
                    </div>
                </Section>

                {/* Per-client table */}
                <Section title="Per client" icon={PoundSterling}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.5px]" data-testid="profitability-clients-table">
                            <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                <tr>
                                    <SortHeader label="Client" sortKey="client_name" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                    <SortHeader label="Revenue" sortKey="revenue_to_date" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                    <SortHeader label="Cost" sortKey="cost_to_date" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                    <SortHeader label="GP" sortKey="gp_to_date" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                    <SortHeader label="GP%" sortKey="gp_pct" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                    <SortHeader label="Avg cost/mo" sortKey="trailing_avg_cost" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                    <th className="text-right font-medium px-4 py-3">Forecast £/mo</th>
                                    <th className="text-right font-medium px-4 py-3">Forecast GP ({horizon}mo)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedClients.map(c => (
                                    <ClientRow key={c.client_id} c={c} horizon={horizon} canEdit={canEdit}
                                        onNavigate={() => navigate(`/clients/${c.client_id}`)}
                                        onSaveOverride={saveOverride} />
                                ))}
                                {sortedClients.length === 0 && (
                                    <tr><td colSpan={8} className="py-8 text-center text-[color:var(--bm-text-muted)]">No invoiced or costed clients yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Section>

                {/* Monthly quick-entry grid */}
                {canCreate && (
                    <Section title="Quick monthly cost entry" icon={Pencil}>
                        <div className="text-[11.5px] text-[color:var(--bm-text-muted)] mb-4">Click a cell to log or edit that client's cost for the month.</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12px]" data-testid="cost-grid-table">
                                <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                    <tr>
                                        <th className="text-left font-medium px-3 py-2 sticky left-0 bg-white">Client</th>
                                        {gridMonths.map(m => <th key={m} className="text-center font-medium px-3 py-2 whitespace-nowrap">{monthLabel(m)}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedClients.map(c => (
                                        <tr key={c.client_id} className="border-b border-[color:var(--bm-border)] last:border-0">
                                            <td className="px-3 py-2 font-medium text-[color:var(--bm-text)] sticky left-0 bg-white whitespace-nowrap">{c.client_name}</td>
                                            {gridMonths.map(m => {
                                                const entry = entriesByClientMonth[`${c.client_id}__${m}`];
                                                return (
                                                    <td key={m} className="px-1.5 py-1.5 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => setGridEntry({ clientId: c.client_id, period: m, entry: entry || null })}
                                                            data-testid={`cost-grid-cell-${c.client_id}-${m}`}
                                                            className={`w-full rounded-md px-2 py-1.5 transition hover:bg-[color:var(--bm-bg-mute)] ${entry ? 'text-[color:var(--bm-text)]' : 'text-[color:var(--bm-text-subtle)] border border-dashed border-[color:var(--bm-border)]'}`}
                                                        >
                                                            {entry
                                                                ? (entry.entry_mode === 'credits'
                                                                    ? `${Math.round(entry.credits_used).toLocaleString()} cr · ${GBP(entry.amount)}`
                                                                    : GBP(entry.amount))
                                                                : '+ Log'}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {sortedClients.length === 0 && (
                                        <tr><td colSpan={gridMonths.length + 1} className="py-8 text-center text-[color:var(--bm-text-muted)]">No clients yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Section>
                )}
            </div>

            <CostEntryDialog
                open={logCostOpen} onOpenChange={setLogCostOpen}
                clients={rawClients} defaultRate={settings?.profitability_credit_unit_cost}
                onSaved={refreshAfterCostChange}
            />
            {gridEntry && (
                <CostEntryDialog
                    open={!!gridEntry} onOpenChange={(v) => { if (!v) setGridEntry(null); }}
                    clientId={gridEntry.clientId} period={gridEntry.period} entry={gridEntry.entry}
                    defaultRate={settings?.profitability_credit_unit_cost}
                    onSaved={refreshAfterCostChange}
                />
            )}
        </div>
    );
}

function monthLabel(ym) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

function ClientRow({ c, horizon, canEdit, onNavigate, onSaveOverride }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(c.forecast_monthly_cost);
    const forecastGp = ((c.forecast_monthly_revenue || 0) - (c.forecast_monthly_cost || 0)) * horizon;

    return (
        <tr className="border-b border-[color:var(--bm-border)] last:border-0 hover:bg-[color:var(--bm-bg-mute)]" data-testid={`profitability-row-${c.client_id}`}>
            <td className="py-2.5 px-4 font-medium text-[color:var(--bm-text)] cursor-pointer" onClick={onNavigate}>{c.client_name}</td>
            <td className="py-2.5 px-4 text-right text-[color:var(--bm-text-muted)]">{GBP(c.revenue_to_date)}</td>
            <td className="py-2.5 px-4 text-right text-[color:var(--bm-text-muted)]">{GBP(c.cost_to_date)}</td>
            <td className="py-2.5 px-4 text-right font-medium text-[color:var(--bm-text)]">{GBP(c.gp_to_date)}</td>
            <td className="py-2.5 px-4 text-right">{c.gp_pct}%</td>
            <td className="py-2.5 px-4 text-right text-[color:var(--bm-text-muted)]">{GBP(c.trailing_avg_cost)}</td>
            <td className="py-2.5 px-4 text-right">
                {editing ? (
                    <div className="flex items-center justify-end gap-1.5">
                        <Input type="number" value={val ?? ''} onChange={e => setVal(e.target.value)}
                               className="h-7 w-24 text-right text-[12px]" data-testid={`forecast-override-input-${c.client_id}`} autoFocus />
                        <Button size="sm" className="h-7 px-2 text-[11px]" style={{ background: 'var(--bm-primary)' }}
                                onClick={() => { onSaveOverride(c.client_id, val); setEditing(false); }} data-testid={`forecast-override-save-${c.client_id}`}>Save</Button>
                    </div>
                ) : (
                    <div className="inline-flex items-center gap-1.5 justify-end w-full">
                        {canEdit && c.has_override && (
                            <button type="button" onClick={() => onSaveOverride(c.client_id, null)}
                                    className="text-[10.5px] text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)] underline"
                                    data-testid={`forecast-override-clear-${c.client_id}`}>
                                Use average instead
                            </button>
                        )}
                        <button type="button" onClick={() => canEdit && setEditing(true)} className="inline-flex items-center gap-1.5"
                                data-testid={`forecast-override-display-${c.client_id}`}>
                            {GBP(c.forecast_monthly_cost)}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.has_override ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                                {c.has_override ? 'your figure' : 'using average'}
                            </span>
                            {canEdit && <Pencil size={11} className="opacity-50" />}
                        </button>
                    </div>
                )}
            </td>
            <td className="py-2.5 px-4 text-right font-medium text-[color:var(--bm-text)]">{GBP(forecastGp)}</td>
        </tr>
    );
}
